import React, { ComponentProps, JSX, useCallback, useEffect, useId, useRef, useState } from 'react';
import { QuestionOutlineIcon } from '@chakra-ui/icons';
import { Tooltip } from '@chakra-ui/react';

import { TooltipManager } from './TooltipManager';

const TooltipHelp = ({
  label,
  bgColor,
  color,
  ...props
}: Omit<ComponentProps<typeof Tooltip>, 'children'>): JSX.Element => {
  const [isClickOpen, setIsClickOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();
  const manager = TooltipManager.getInstance();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isClickOpen) {
      manager.openTooltip(tooltipId);
    }
    setIsClickOpen(!isClickOpen);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handleClose = useCallback(() => {
    setIsClickOpen(false);
    manager.closeTooltip(tooltipId);
  }, [manager, tooltipId]);

  // Register/unregister with manager
  useEffect(() => {
    const currentTooltipId = tooltipId;
    manager.register(currentTooltipId, handleClose);

    return () => {
      manager.unregister(currentTooltipId);
    };
  }, [manager, handleClose, tooltipId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node) && isClickOpen) {
        handleClose();
      }
    };

    if (isClickOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isClickOpen, handleClose]);

  return (
    <Tooltip
      bg={bgColor}
      placement="top-end"
      label={label}
      color={color}
      fontSize="12px"
      hasArrow
      closeOnScroll
      closeOnEsc
      isOpen={isClickOpen || undefined}
      onClose={handleClose}
      ref={tooltipRef}
      {...props}
    >
      <QuestionOutlineIcon
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        color={'black'}
        data-testid="tooltip-trigger-icon"
        ml={2}
        cursor="pointer"
      />
    </Tooltip>
  );
};

export default TooltipHelp;
